import type { Chord } from '@/types';

/**
 * OTO-22: pure lookahead-window selection for the audio-clock scheduler
 * (`useAutoPlay.ts`). Given a monotonic cursor and a `[windowStart,
 * windowEnd]` transport-time window, decides which not-yet-handled chords
 * to schedule now, which to skip (stall recovery), and where the cursor
 * lands next. No Tone import, no timers -- pure data in, pure data out.
 */

/** Lookahead window size (seconds) the scheduler queues attacks within. */
export const LOOKAHEAD_S = 0.12;
/** Scheduler poll interval (ms). */
export const POLL_MS = 25;
/**
 * Late-chord grace (seconds): a chord older than `windowStart - grace` is
 * assumed to be the result of a main-thread stall longer than the lookahead
 * window and is skipped silently rather than burst-fired -- this is the
 * late-skip-not-burst policy that prevents the note-burst bug on recovery.
 */
export const LATE_GRACE_S = 0.05;

export interface SelectChordsParams {
  chords: Chord[];
  /** Index of the last chord already handled (scheduled or skipped). */
  cursor: number;
  /** Start of the lookahead window (transport seconds), i.e. "now". */
  windowStart: number;
  /** End of the lookahead window (transport seconds). */
  windowEnd: number;
  /** Late-chord grace threshold (seconds); see `LATE_GRACE_S`. */
  grace: number;
  /**
   * Loop-end clamp (B1): when a loop region is active, no chord with
   * `startTime >= hardStop` is ever selected, even if it falls inside
   * `[windowStart, windowEnd]`. Prevents the lookahead from scheduling
   * ghost notes past `loopEnd` before the wrap lands.
   */
  hardStop?: number;
}

export interface ScheduledChord {
  index: number;
  /** Transport time (seconds) to schedule the attack at. */
  transportTime: number;
}

export interface SelectChordsResult {
  toSchedule: ScheduledChord[];
  /** Indexes of chords skipped as stale (older than `windowStart - grace`). */
  toSkip: number[];
  /** New cursor value -- index of the last chord handled this call. */
  nextCursor: number;
}

/**
 * Selects which chords after `cursor` should be scheduled or skipped for one
 * poll tick. Chords are assumed sorted ascending by `startTime`.
 *
 * Three zones relative to `windowStart`:
 * - `startTime < windowStart - grace`: stale (stall recovery) -> skipped.
 * - `windowStart - grace <= startTime < windowStart`: within grace ->
 *   scheduled "now" (clamped to `windowStart`, since its own startTime has
 *   already passed and can't be scheduled in the past).
 * - `windowStart <= startTime <= windowEnd`: scheduled at its exact time.
 *
 * Iteration stops (leaving the cursor where it is) at the first chord whose
 * `startTime` exceeds `windowEnd`, or -- when `hardStop` is set -- at the
 * first chord at or past `hardStop`, so a loop's lookahead can never queue a
 * chord past `loopEnd` before the wrap lands.
 */
export function selectChords(params: SelectChordsParams): SelectChordsResult {
  const { chords, cursor, windowStart, windowEnd, grace, hardStop } = params;
  const toSchedule: ScheduledChord[] = [];
  const toSkip: number[] = [];
  let nextCursor = cursor;
  const lateThreshold = windowStart - grace;

  for (let i = cursor + 1; i < chords.length; i++) {
    const { startTime } = chords[i];

    if (hardStop !== undefined && startTime >= hardStop) break;
    if (startTime > windowEnd) break;

    if (startTime < lateThreshold) {
      toSkip.push(i);
    } else if (startTime < windowStart) {
      toSchedule.push({ index: i, transportTime: windowStart });
    } else {
      toSchedule.push({ index: i, transportTime: startTime });
    }
    nextCursor = i;
  }

  return { toSchedule, toSkip, nextCursor };
}

/**
 * Strict-`<` counterpart to `findLastPlayedChordIndex` (`useAutoPlay.ts`),
 * used ONLY to seed the scheduler cursor after a loop wrap (M1). A wrap
 * must NOT use `findLastPlayedChordIndex`'s `<=` semantics: that would mark
 * a chord positioned exactly at `loopStart` as already played and drop it
 * on every pass (contract #7). Seek keeps `<=` (OTO-21 preservation) --
 * only the wrap path uses this helper.
 */
export function lastChordIndexStrictlyBefore(chords: Chord[], time: number): number {
  let lastIndex = -1;
  for (let i = 0; i < chords.length; i++) {
    if (chords[i].startTime < time) {
      lastIndex = i;
    } else {
      break;
    }
  }
  return lastIndex;
}
